import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Keeps the product-group (colour) picker's current tile in sync with the selected variant.
 *
 * The variant picker re-fetches the full product page on every in-product variant switch and
 * broadcasts it on the `variant:update` event (see assets/variant-picker.js). This component is one
 * more subscriber on that event — the same seam media-gallery / product-price / product-inventory use
 * — so the picker itself stays generic and unpatched. It morphs only its own `.product-group` from the
 * fetched document: the server has already rendered the current (`is-current`) tile with the newly
 * selected variant's image and left the attribute-sourced heading unchanged
 * (snippets/product-group-swatches.liquid), so a diff-morph updates the one tile image without
 * touching sibling tiles, the heading, or layout. No extra network request — it reuses the picker's
 * fetch. The scoped CSS is already on the page (the block renders in the initial PDP tree), so the
 * morph needs no stylesheet priming.
 *
 * @extends Component
 */
export class ProductGroupMedia extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    // The event bubbles from the variant picker to the shared product section; listen there, matching
    // media-gallery. `dialog` covers the quick-view surface for parity, harmless when absent.
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Morphs the current `.product-group` to match the freshly-fetched page for the selected variant.
   *
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const current = this.querySelector('.product-group');
    const next = source.querySelector('.product-group');
    if (!current || !next) return;

    morph(current, next);
  };
}

if (!customElements.get('product-group-media')) {
  customElements.define('product-group-media', ProductGroupMedia);
}
